import { Component, type ErrorInfo, type ReactNode } from 'react';
import { AlertOctagon } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Alert, AlertBody, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';

function BoundaryFallback({ onReset }: { readonly onReset: () => void }) {
  const { t } = useTranslation();
  return (
    <Alert tone="danger" role="alert" className="m-4">
      <AlertOctagon aria-hidden="true" className="mt-0.5 size-5 shrink-0" />
      <AlertBody className="flex flex-col gap-2">
        <AlertTitle>{t('errors.boundaryTitle')}</AlertTitle>
        <p>{t('errors.boundaryBody')}</p>
        <div>
          <Button size="sm" variant="outline" onClick={onReset}>
            {t('common.reload')}
          </Button>
        </div>
      </AlertBody>
    </Alert>
  );
}

interface Props {
  readonly children: ReactNode;
  readonly onError?: (error: Error, info: ErrorInfo) => void;
}

interface State {
  readonly error: Error | null;
}

/**
 * Contains a render error to one region.
 *
 * The fallback never prints the error text: a message can carry data from a
 * response, and CLAUDE.md §1.6 keeps that out of anything a bystander can read
 * over a shoulder. The real error goes to the console for a developer.
 */
export class ErrorBoundary extends Component<Props, State> {
  override state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    this.props.onError?.(error, info);
    console.error('Render error contained by ErrorBoundary:', error, info.componentStack);
  }

  private readonly reset = () => this.setState({ error: null });

  override render(): ReactNode {
    if (this.state.error) return <BoundaryFallback onReset={this.reset} />;
    return this.props.children;
  }
}
